import { useEffect } from 'react';
import { SignedIn, SignedOut } from '@clerk/clerk-react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import Image from '@/components/ui/Image';
import { LinkLoader } from '@/components/LinkLoader';
import { safeRedirectUrl } from '@/lib/auth-redirect';
import { CLERK_PUBLISHABLE_KEY } from '@/lib/env';

/**
 * A signed-in user on an auth page goes to the allowed `redirect_url` when there is
 * one (e.g. Clerk's OAuth authorize endpoint, so an MCP connector in Claude.ai or
 * ChatGPT can finish its consent flow), otherwise home. The redirect is a full page
 * load because the target is a server endpoint on Clerk's origin, not an SPA route.
 */
function SignedInRedirect() {
  const location = useLocation();
  const target = safeRedirectUrl(location, window.location.origin, CLERK_PUBLISHABLE_KEY);

  useEffect(() => {
    if (target) window.location.assign(target);
  }, [target]);

  return target ? null : <Navigate to="/" replace />;
}

export default function AuthLayout() {
  return (
    <>
      <SignedIn>
        <SignedInRedirect />
      </SignedIn>

      <SignedOut>
        <div className="flex min-h-svh bg-white">
          {/* Branding Panel */}
          <div
            data-testid="auth-branding"
            className="relative flex w-1/2 flex-col items-center justify-center gap-8 overflow-hidden bg-linear-to-br from-slate-900 via-slate-800 to-slate-900 px-12 max-lg:hidden"
          >
            {/* Subtle teal glow */}
            <div className="absolute top-1/4 left-1/2 size-96 -translate-x-1/2 rounded-full bg-teal-500/10 blur-[120px]" />

            {/* Logo + wordmark */}
            <div className="z-10 flex items-center gap-3">
              <Image src="/favicon.png" alt="" width={40} height={40} />
              <h1 className="text-3xl font-black tracking-tight text-white">Vértice</h1>
            </div>

            {/* Mascot */}
            <div data-testid="auth-mascot-desktop" className="animate-mascot-float z-10">
              <Image
                src="/mascot/link-core.png"
                alt="Link - Assistente Vértice"
                width={220}
                height={220}
                className="drop-shadow-[0_0_40px_rgba(14,165,168,0.3)]"
                priority
              />
            </div>

            {/* Wave loader */}
            <div className="z-10">
              <LinkLoader size="inline" />
            </div>

            {/* Tagline */}
            <p className="z-10 text-sm font-semibold tracking-wide text-slate-400">
              Finanças conectadas com inteligência.
            </p>
          </div>

          {/* Form Panel */}
          <div className="flex min-h-svh flex-1 flex-col bg-white lg:items-center lg:justify-center lg:px-6 lg:py-12">
            <div className="flex min-h-svh w-full flex-1 flex-col px-5 pt-[max(1.5rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))] lg:min-h-0 lg:items-center lg:justify-center lg:p-0">
              <main
                data-testid="auth-form"
                className="flex w-full flex-1 flex-col items-center justify-center lg:flex-none lg:justify-center"
              >
                <Outlet />
              </main>
            </div>
          </div>
        </div>
      </SignedOut>
    </>
  );
}
